// src/context/AuthContext.js
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

const AuthContext = createContext(null);

const REDIRECT_PATH_KEY = "auth_redirect_path";
const EMPTY_GROUPS = [];

export const AppAuthProvider = ({ children }) => {
    const oidcAuth = useOidcAuth();
    const hasRestoredPath = useRef(false);

    // Strip the ?code&state Cognito appends after login, and send the user
    // back to whatever page they were on before being bounced to log in
    // (recorded by login() below). react-oidc-context/oidc-client-ts owns
    // token persistence and silent renewal itself now - see index.js's
    // automaticSilentRenew - so there's nothing else to restore here.
    useEffect(() => {
        if (!oidcAuth.isAuthenticated || oidcAuth.isLoading) return;

        const url = new URL(window.location.href);
        if (url.searchParams.has("code") || url.searchParams.has("state")) {
            url.searchParams.delete("code");
            url.searchParams.delete("state");
            window.history.replaceState({}, document.title, url.toString());
        }

        if (!hasRestoredPath.current) {
            hasRestoredPath.current = true;
            const redirectPath = sessionStorage.getItem(REDIRECT_PATH_KEY);
            if (redirectPath) {
                sessionStorage.removeItem(REDIRECT_PATH_KEY);
                const currentPath =
                    window.location.pathname + window.location.search;
                if (redirectPath !== currentPath) {
                    window.location.replace(redirectPath);
                }
            }
        }
    }, [oidcAuth.isAuthenticated, oidcAuth.isLoading]);

    // Depending on oidcAuth.signinRedirect/.removeUser specifically (rather
    // than the whole oidcAuth object, which react-oidc-context reconstructs
    // on every render) keeps these stable across renders.
    const login = useCallback(async () => {
        sessionStorage.setItem(
            REDIRECT_PATH_KEY,
            window.location.pathname + window.location.search,
        );
        await oidcAuth.signinRedirect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oidcAuth.signinRedirect]);

    const logout = useCallback(async () => {
        sessionStorage.removeItem(REDIRECT_PATH_KEY);
        const clientId = "2rt245npheca1rkm9umdqd79e";
        const logoutUri = window.location.origin;
        const cognitoDomain =
            "https://ap-southeast-2iwiwrb0qr.auth.ap-southeast-2.amazoncognito.com";
        await oidcAuth.removeUser();
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oidcAuth.removeUser]);

    const profile = oidcAuth.user?.profile;
    const idToken = oidcAuth.user?.id_token;
    const accessToken = oidcAuth.user?.access_token;

    // Every one of these used to be reconstructed (new array/object/function
    // identity) on every render, regardless of whether the underlying auth
    // state had actually changed. Since the context value is also rebuilt
    // from them, that meant every useAuth() consumer - and every effect
    // depending on `tokens` to decide when to (re)fetch - re-ran on every
    // unrelated render anywhere above this provider, not just on real auth
    // changes. Memoising on the actual primitive values fixes that.
    const groups = useMemo(
        () => profile?.["cognito:groups"] || EMPTY_GROUPS,
        [profile],
    );
    const isInGroup = useCallback((group) => groups.includes(group), [groups]);
    const tokens = useMemo(
        () => (idToken ? { idToken, accessToken } : null),
        [idToken, accessToken],
    );
    const user = useMemo(() => profile || null, [profile]);

    const value = useMemo(
        () => ({
            isAuthenticated: oidcAuth.isAuthenticated,
            isLoading: oidcAuth.isLoading,
            user,
            groups,
            isInGroup,
            login,
            logout,
            tokens,
        }),
        [
            oidcAuth.isAuthenticated,
            oidcAuth.isLoading,
            user,
            groups,
            isInGroup,
            login,
            logout,
            tokens,
        ],
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
